import { useCallback, useEffect, useMemo, useRef } from "react";

// Shared file-value logic behind FileUploadBar and FileUploadDropzone —
// resolving a display URL/name/size from either a freshly-picked File or an
// already-uploaded { url, name } value, independent of how each renders it.
export function useFileUpload({ value, onChange, baseUrl = "" }) {
  const inputRef = useRef(null);

  const objectUrl = useMemo(() => {
    if (value?.file instanceof File) {
      return URL.createObjectURL(value.file);
    }
    return null;
  }, [value]);

  useEffect(
    () => () => objectUrl && URL.revokeObjectURL(objectUrl),
    [objectUrl],
  );

  const displayName =
    (value?.file && value.file.name) ||
    value?.name ||
    (value?.url ? value.url.split("/").pop() : null);

  const effectiveUrl = useMemo(() => {
    if (objectUrl) return objectUrl;
    if (value?.url && value.url !== "undefined") {
      try {
        new URL(value.url);
        return value.url;
      } catch {
        return baseUrl
          ? `${baseUrl.replace(/\/$/, "")}/${value.url.replace(/^\//, "")}`
          : value.url;
      }
    }
    return null;
  }, [objectUrl, value, baseUrl]);

  const sizeText = useMemo(() => {
    const bytes = value?.file?.size ?? value?.size;
    if (!bytes && bytes !== 0) return null;
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024)
      return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
    return `${(bytes / 1024 / 1024 / 1024).toFixed(1)} GB`;
  }, [value]);

  const isImage = useMemo(() => {
    const mime = value?.file?.type || "";
    if (mime.startsWith("image/")) return true;
    const name = (displayName || "").toLowerCase();
    return /(\.png|\.jpe?g|\.webp|\.gif|\.bmp|\.svg)$/.test(name);
  }, [value, displayName]);

  const hasAnyFile = !!(
    value?.file ||
    (value?.url && value.url !== "undefined")
  );

  const handleFile = useCallback(
    (file) => {
      if (!file) return;
      onChange?.({ file, uploadedAt: new Date() });
    },
    [onChange],
  );

  const onBrowseClick = useCallback(() => inputRef.current?.click(), []);

  return {
    inputRef,
    effectiveUrl,
    displayName,
    sizeText,
    isImage,
    hasAnyFile,
    handleFile,
    onBrowseClick,
  };
}
